import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import { withStyles } from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import TabPagamento from "./TabPagamento";
import TabObservacoes from "./TabObservacoes";
import { findConfiguracaoUnidade } from "../../../../../../services/OdontogramaService";
import CloseIcon from "../../../../../../components/Icon/Close";
import Dialog from "../../../../../../components/Dialog/Dialog";
import { Button } from "../../../../../../components/ui/Buttons";
import { InputForm } from "../../../../../../components/Modal/Input";
import { defaultErrors } from "../../../../../../stores/Orcamento.store";
import MultiToggleButtons from "../../../../../../components/Button/MultiToggleButtons";
import PrintIcon from "../../../../../../components/Icon/Print";
import colors from "../../../../../../template/Colors";
import { checkUserRoles } from "../../../../../../components/RoleProtection";
import Auditoria from "../../../../../../components/Auditoria/Auditoria";
import { findAllOrcamentoAuditByOrcamentoId } from "../../../../../../services/AuditoriaService";
import { TextFieldSearch } from "../../../../../../components/TextField";
import { findAllProfissionaisExternosAndInterno } from "../../../../../../services/OrcamentoService";

const ModalOrcamento = observer(
  ({
    classes,
    open,
    title,
    orcamentoStore,
    children,
    handleSave,
    onClose,
    isLoadingSave,
    setIsLoadingSave,
    handlePrint,
    auditoriaStore,
  }) => {
    const { orcamento, handleFieldModal } = orcamentoStore;
    const { numero, id, statusOrcamentoOdontologico } = orcamento || {};
    const usuarioLogado = JSON.parse(localStorage[`_immortal|usuarioLogado`] || null);
    const profissionalSaudeLogado = JSON.parse(localStorage[`_immortal|profissional_saude_logado`] || null);
    const canReadGerenciadorOrcamento = usuarioLogado.authorities.some(a =>
      a.authority === 'ROLE_GERENCIADOR_ORCAMENTO_READ');

    const tabs = useMemo(() => {
      if(!canReadGerenciadorOrcamento) {
        return {
          OBSERVACOES: 0,
          AUDITORIA: 1
        };
      }
      return {
        PAGAMENTO: 0,
        OBSERVACOES: 1,
        AUDITORIA: 2
      };
    }, [canReadGerenciadorOrcamento]);

    const [tabSelected, setTabSelected] = useState(tabs.PAGAMENTO);
    const [configUnidade, setConfigUnidade] = useState(null);

    useEffect(() => {
      getConfigUnidade();
      if(!orcamentoStore.orcamento?.id) {
        orcamentoStore.orcamento.orcamentista = profissionalSaudeLogado;
      }
      orcamentoStore.errors = defaultErrors;
    }, []);

    useEffect(() => {
      if(tabSelected === tabs.AUDITORIA) {
        loadAudit();
      }
    }, [tabSelected]);

    const changeTabSelected = (tabSelected) => {
      setTabSelected(tabSelected);
    };

    const getConfigUnidade = async () => {
      const configUnidade = await findConfiguracaoUnidade();
      setConfigUnidade(configUnidade);
    };

    const loadAudit = async () => {
      auditoriaStore.auditoriaProps = {
        ...auditoriaStore.auditoriaProps,
        auditoria: "o orçamento",
        auditoriaTela: "orcamentos",
        pageNumber: 0,
        lastPage: false,
        auditorias: [],
      };

      auditoriaStore.load = {
        query: findAllOrcamentoAuditByOrcamentoId,
        variableName: "orcamentoId",
        variableId: orcamento?.id,
      };

      await auditoriaStore.loadAuditItems();
    };

    const handleAction = async (action) => {
      try {
        setIsLoadingSave(true);
        await handleSave(action);
      } catch (e) {
        throw e;
      } finally {
        setIsLoadingSave(false);
      }
    };

    const renderTab = () => {
      const tabsWithoutPagamento = {
        [tabs.OBSERVACOES]: (
          <TabObservacoes
            orcamento={orcamento}
            handleFieldModal={handleFieldModal}
          />
        ),
        [tabs.AUDITORIA]: <Auditoria />,
      };

      const tabsDefault = {
        [tabs.PAGAMENTO]: (
          <TabPagamento
            configUnidade={configUnidade}
            orcamento={orcamento}
            handleFieldModal={handleFieldModal}
          />
        ),
        [tabs.OBSERVACOES]: (
          <TabObservacoes
            orcamento={orcamento}
            handleFieldModal={handleFieldModal}
          />
        ),
        [tabs.AUDITORIA]: <Auditoria />,
      };

      return canReadGerenciadorOrcamento
        ? tabsDefault[tabSelected]
        : tabsWithoutPagamento[tabSelected];
    };

    const permiteSalvar = async () => {
      const possuiPermissao = await checkUserRoles([
        "ROLE_ORCAMENTO_CREATE",
        "ROLE_ORCAMENTO_UPDATE",
      ]);
      return possuiPermissao;
    };

    const loadProfissionaisSaude = async (search, loadedOptions, { page }) => {
      const pageableDTO = {
        pageNumber: page,
        pageSize: 10,
        sortDir: "ASC",
        sortField: "nome",
      };
  
      try {
        const { content, last } = await findAllProfissionaisExternosAndInterno({
          pageableDTO,
          search,
          ativo: true
        });
        return {
          options: content,
          hasMore: !last,
          additional: {
            page: page + 1,
          },
        };
      } catch (error) {
        console.error(error);
      }
    }

    return (
      <Dialog
        open={open}
        onClose={onClose}
        classes={{
          paper: classes.dialog,
        }}
      >
        <div className={classes.header}>
          {title}
          <div className={classes.buttonsHeader}>
            <Button
              id="prt-mor-btn-imp"
              className={classes.buttonImprimir}
              onClick={() => handlePrint(orcamento, statusOrcamentoOdontologico?.tipo)}
              disabled={isLoadingSave}
            >
              <PrintIcon />
            </Button>
            <Button id="prt-mor-btn-fch" bgColor="#FB7676" shape="circle" onClick={onClose}>
              <CloseIcon />
            </Button>
          </div>
        </div>
        <div className={classes.content}>
          {!!id && <div className={classes.contentField}>
            <span className={classes.titleField}>Número:</span>
            { numero }
          </div>}

          <div className={classes.contentProfissionais}>
            <div className={classes.fieldProfissional}>
              <span className={classes.titleInput}>Orçamentista:</span>
              <TextFieldSearch
                id="prt-mor-sel-orc"
                placeholder='Selecione'
                classIcons={classes.classIcons}
                classInput={classes.reactSelect}
                classNotched={classes.notchedOutline}
                loadOptions={loadProfissionaisSaude}
                getOptionLabel={option => option?.nome}
                getOptionValue={option => option?.id}
                value={orcamentoStore.orcamento.orcamentista}
                classes={{ paper: classes.menuHeight }}
                onChange={e => orcamentoStore.handleFieldModal('orcamentista', e)}
                withPaginate
                debounceTimeout={300}
                additional={{
                  page: 0,
                }}
                menuPosition='fixed'
                isClearable={false}
              />
            </div>

            <div className={classes.fieldProfissional}>
              <span className={classes.titleInput}>Profissional Executante:</span>
              <TextFieldSearch
                id="prt-mor-sel-pex"
                placeholder='Selecione'
                classIcons={classes.classIcons}
                classInput={classes.reactSelect}
                classNotched={classes.notchedOutline}
                loadOptions={loadProfissionaisSaude}
                getOptionLabel={option => option?.nome}
                getOptionValue={option => option?.id}
                value={orcamentoStore.orcamento.profissionalExecutante}
                classes={{ paper: classes.menuHeight }}
                onChange={e => orcamentoStore.handleFieldModal('profissionalExecutante', e)}
                withPaginate
                debounceTimeout={300}
                additional={{
                  page: 0,
                }}
                menuPosition='fixed'
              />
            </div>
          </div>

          <MultiToggleButtons
            id="prt-mor-btn-tab"
            options={
              canReadGerenciadorOrcamento ? 
              ["Pagamento", "Observações", "Auditoria"] 
              : 
              ["Observações", "Auditoria"]
            }
            tabSelected={tabSelected}
            changeTabSelected={changeTabSelected}
          />
          {renderTab()}
          {tabSelected !== tabs.AUDITORIA && children}
        </div>
        <div className={classes.buttons}>
          <Button
            id="prt-mor-btn-slv"
            bgColor="#707C97"
            onClick={() => handleAction(orcamento?.tipo === "ODONTOLOGICO" ?statusOrcamentoOdontologico?.tipo : null)}
            width="205px"
            disabled={isLoadingSave || !permiteSalvar() || statusOrcamentoOdontologico?.tipo === "APROVADO"}
            isLoading={isLoadingSave}
          >
            Salvar
          </Button>
          <Button
            id="prt-mor-btn-apr"
            bgColor="#26ACAA"
            onClick={() => handleAction("APROVADO")}
            width="205px"
            disabled={isLoadingSave || statusOrcamentoOdontologico?.tipo === "APROVADO"}
            isLoading={isLoadingSave}
          >
            Aprovar
          </Button>
        </div>
      </Dialog>
    );
  }
);

const styles = {
  dialog: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px 32px 32px 32px",
    width: "100%",
    maxWidth: "800px",
    height: "100%",
    maxHeight: "700px",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "18px",
    fontWeight: "700",
    color: "#333333",
  },
  content: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
  },
  contentField: {
    display: "flex",
    flexDirection: "column",
  },
  titleField: {
    color: "#868686",
  },
  buttonImprimir: {
    backgroundColor: colors.commons.secondary,
    width: "40px",
    height: "40px",
    color: "white",
    "&:hover": {
      background: colors.primary.dark,
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px !important",
  },
  buttonsHeader: {
    display: "flex",
    gap: "10px",
  },
  contentProfissionais: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
  },
  fieldProfissional: {
    flex: 1,
    maxWidth: "50%",
  },
};

const ModalOrcamentoWithStyles = withStyles(styles)(ModalOrcamento);
export default inject(
  "odontogramaStore",
  "prontuarioStore",
  "orcamentoStore",
  "auditoriaStore"
)(ModalOrcamentoWithStyles);
